import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Button from '../../../Components/Button/Button';
import { getApi, postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import { readSubscriptionResponse } from '../../../Utils/subscriptionTrial';
import { updateStoredSubscriptionPlan } from '../../../Utils/propertyLimit';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Load Stripe with error handling - prevents app crash when Stripe.js fails to load (network, ad blocker, etc.)
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey).catch((err) => {
      console.warn('Stripe failed to load. Payment may be unavailable.', err?.message || err);
      return null;
    })
  : null;

const elementOptions = {
  style: {
    base: {
      fontSize: '14px',
      color: '#2c2c2c',
      fontFamily:
        "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      '::placeholder': {
        color: '#9aa0a6',
        fontFamily: "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      },
    },
    invalid: { color: '#dc3545' },
  },
};

// Prices come back as plain numbers (11.99, 29). Keep whole amounts clean: $29, not $29.00
const formatPrice = (price) => {
  const value = Number(price);
  if (!Number.isFinite(value)) return '$0';
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
};

const formatInterval = (interval) => {
  const value = String(interval || 'month').toLowerCase();
  if (value === 'monthly') return 'month';
  if (value === 'yearly' || value === 'annually') return 'year';
  if (value === 'weekly') return 'week';
  return value;
};

// The plans API only returns name/price/interval, so the tier copy lives here,
// keyed by the plan name coming back from /subscriptions/plans.
const PLAN_TIER_DETAILS = {
  basic: { range: '1 - 5 properties', tagline: 'For landlords getting started' },
  standard: { range: '6 - 15 properties', tagline: 'For growing portfolios' },
  enterprise: { range: '16+ properties', tagline: 'For large portfolios' },
};

// "Platform fee" is not a tier the landlord chooses, so it is kept out of the picker.
const isSelectableTier = (plan) => !/platform\s*fee/i.test(String(plan?.name || ''));

const getTierDetails = (plan) => PLAN_TIER_DETAILS[String(plan?.name || '').trim().toLowerCase()] || null;

// `mode` is 'pay' for a normal plan switch and 'setup' when the API hands back a
// SetupIntent instead (a switch made while the trial is still running - the card
// is saved now and charged when the trial ends).
function PlanPaymentForm({ plan, clientSecret, mode = 'pay', onPaymentSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);

  const amountText = formatPrice(plan?.price);
  const intervalLabel = formatInterval(plan?.interval);
  const isSetupMode = mode === 'setup';

  // Browser autofill fills the whole card in one shot, so the field jumps from
  // empty straight to complete in a single change event. Moving focus in the
  // middle of that cancels the fill and leaves the card input stuck - it looks
  // disabled and stops accepting typing. Typing produces a change per
  // keystroke, so only auto-advance once we have seen more than one change,
  // and never onto a field autofill has already filled.
  const numberEdits = useRef(0);
  const expiryEdits = useRef(0);

  const handleCardNumberChange = (event) => {
    numberEdits.current = event.empty ? 0 : numberEdits.current + 1;
    if (!event.complete || numberEdits.current < 2 || !elements) return;
    const expiryElement = elements.getElement(CardExpiryElement);
    if (expiryElement) setTimeout(() => expiryElement.focus(), 50);
  };

  const handleExpiryChange = (event) => {
    expiryEdits.current = event.empty ? 0 : expiryEdits.current + 1;
    if (!event.complete || expiryEdits.current < 2 || !elements) return;
    const cvcElement = elements.getElement(CardCvcElement);
    if (cvcElement) setTimeout(() => cvcElement.focus(), 50);
  };

  const handlePay = async (e) => {
    e.preventDefault();

    if (!clientSecret) {
      toast.error('Payment is not ready yet. Please wait a moment and try again.');
      return;
    }
    if (!stripe || !elements) return;

    setIsLoading(true);
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        toast.error('Card element not ready. Please try again.');
        return;
      }

      const { error, paymentIntent, setupIntent } = isSetupMode
        ? await stripe.confirmCardSetup(clientSecret, { payment_method: { card: cardNumber } })
        : await stripe.confirmCardPayment(clientSecret, { payment_method: { card: cardNumber } });

      if (error) {
        toast.error(error.message || (isSetupMode ? 'We could not save your card. Please try again.' : 'Payment failed. Please try again.'));
        return;
      }

      const intent = isSetupMode ? setupIntent : paymentIntent;
      if (intent?.status === 'succeeded') {
        toast.success('Plan changed successfully');
        onPaymentSuccess?.();
      } else {
        toast.info(`${isSetupMode ? 'Card setup' : 'Payment'} status: ${intent?.status || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err?.message || 'Payment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="payment-form-section plan-payment-form">
      <h2 className="payment-form-title">{isSetupMode ? 'Confirm your card' : 'Complete Payment'}</h2>
      <p className="payment-form-subtitle">
        {isSetupMode ? 'Your card is charged when the free trial ends' : 'Secure payment to switch your plan'}
      </p>

      <div className="payment-summary-card">
        <div className="payment-summary-row">
          <div className="payment-summary-label">{plan?.name ? `${plan.name} plan` : 'Selected plan'}</div>
          <div className="payment-summary-price">{amountText}/{intervalLabel}</div>
        </div>
        <div className="payment-summary-divider" />
        <div className="payment-summary-row total">
          <div className="payment-summary-label">{isSetupMode ? 'Due today' : 'Total'}</div>
          <div className="payment-summary-total">{isSetupMode ? '$0' : amountText}</div>
        </div>
        <div className="payment-summary-footnote">
          {isSetupMode ? `Then ${amountText}/${intervalLabel} once the trial ends` : `Valid for 1 ${intervalLabel}`}
        </div>
      </div>

      <form onSubmit={handlePay} className="payment-card-form" noValidate autoComplete="off" data-form-type="other">
        <div className="payment-field">
          <label className="payment-label">Card Number</label>
          <div className="payment-stripe-input payment-stripe-input-with-icon" data-autocomplete="off">
            <svg className="payment-card-icon" width="20" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0.5" y="3.5" width="23" height="12" rx="1.5" stroke="#9aa0a6" strokeWidth="1"/>
              <rect x="2" y="6" width="20" height="2" rx="1" fill="#9aa0a6"/>
              <rect x="2" y="10" width="8" height="2" rx="1" fill="#9aa0a6"/>
            </svg>
            <CardNumberElement
              options={{ ...elementOptions, disableLink: true }}
              onChange={handleCardNumberChange}
            />
          </div>
        </div>

        <div className="payment-row">
          <div className="payment-field">
            <label className="payment-label">Expiry</label>
            <div className="payment-stripe-input" data-autocomplete="off">
              <CardExpiryElement
                options={{ ...elementOptions, disableLink: true }}
                onChange={handleExpiryChange}
              />
            </div>
          </div>
          <div className="payment-field">
            <label className="payment-label">CVC</label>
            <div className="payment-stripe-input" data-autocomplete="off">
              <CardCvcElement options={{ ...elementOptions, disableLink: true }} />
            </div>
          </div>
        </div>

        <div className="plan-payment-actions">
          <button type="button" className="plan-payment-cancel" onClick={onCancel} disabled={isLoading}>
            Cancel
          </button>
          <Button type="submit" variant="success" className="payment-submit-button" disabled={isLoading || !clientSecret}>
            {isLoading ? 'Processing...' : isSetupMode ? 'Save card' : `Pay ${amountText}`}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Lists every plan from /subscriptions/plans: the one the landlord is on shows as
// "Activated", the rest offer "Change Plan" which opens the Stripe card form.
const SubscriptionPlans = ({ currentPlanName, onPlanChanged }) => {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [initError, setInitError] = useState('');
  const [pendingPlanId, setPendingPlanId] = useState('');
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentMode, setPaymentMode] = useState('pay');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  // Plan awaiting the landlord's "OK" in the confirmation modal.
  const [confirmPlan, setConfirmPlan] = useState(null);

  // Cache the created subscription per plan so opening the same plan twice
  // doesn't create a new subscription every time.
  const subscriptionCacheRef = useRef({});

  const fetchPlans = async () => {
    setLoading(true);
    setInitError('');
    try {
      const plansRes = await getApi('/subscriptions/plans');
      const list =
        plansRes?.data?.plans ||
        plansRes?.data?.data?.plans ||
        plansRes?.plans ||
        (Array.isArray(plansRes?.data) ? plansRes.data : []) ||
        [];

      const availablePlans = list.filter((plan) => plan?.id && isSelectableTier(plan));

      if (!availablePlans.length) {
        throw new Error('No subscription plans are available right now');
      }

      setPlans(availablePlans);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to load subscription plans';
      setInitError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // The plans API flags the active plan, but fall back to matching the name from
  // /subscriptions/details so the badge still shows if the flag is missing.
  const isCurrentPlan = useMemo(() => {
    const activeName = String(currentPlanName || '').trim().toLowerCase();
    return (plan) => {
      if (plan?.is_current_plan === true || plan?.is_current_plan === 1 || plan?.is_current_plan === '1') {
        return true;
      }
      const hasFlaggedPlan = plans.some(
        (p) => p?.is_current_plan === true || p?.is_current_plan === 1 || p?.is_current_plan === '1'
      );
      if (hasFlaggedPlan || !activeName) return false;
      return String(plan?.name || '').trim().toLowerCase() === activeName;
    };
  }, [plans, currentPlanName]);

  const openConfirmModal = (plan) => {
    if (!plan?.id || pendingPlanId) return;
    setConfirmPlan(plan);
  };

  const closeConfirmModal = () => setConfirmPlan(null);

  const confirmChangePlan = () => {
    const plan = confirmPlan;
    setConfirmPlan(null);
    handleChangePlan(plan);
  };

  const handleChangePlan = async (plan) => {
    if (!plan?.id || pendingPlanId) return;

    const cached = subscriptionCacheRef.current[plan.id];
    if (cached?.clientSecret) {
      setPaymentPlan(plan);
      setClientSecret(cached.clientSecret);
      setPaymentMode(cached.mode);
      setPaymentModalOpen(true);
      return;
    }

    setPendingPlanId(plan.id);
    try {
      const changeRes = await postApi('/subscriptions/change-plan', {
        plan_id: plan.id,
      });

      const outcome = readSubscriptionResponse(changeRes);

      // The switch needs a card only when Stripe.js still has an intent to
      // confirm; otherwise it was settled with the card already on file.
      if (outcome.nextAction === 'done' || !outcome.clientSecret) {
        toast.success(changeRes?.message || 'Plan changed successfully');
        updateStoredSubscriptionPlan(plan);
        await fetchPlans();
        await onPlanChanged?.();
        return;
      }

      subscriptionCacheRef.current[plan.id] = {
        clientSecret: outcome.clientSecret,
        mode: outcome.nextAction,
      };
      setPaymentPlan(plan);
      setClientSecret(outcome.clientSecret);
      setPaymentMode(outcome.nextAction);
      setPaymentModalOpen(true);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to change the plan';
      toast.error(msg);
    } finally {
      setPendingPlanId('');
    }
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentPlan(null);
    setClientSecret('');
    setPaymentMode('pay');
  };

  const handlePaymentSuccess = async () => {
    // A paid plan invalidates the cached client secrets - they belong to the old subscription.
    subscriptionCacheRef.current = {};
    updateStoredSubscriptionPlan(paymentPlan);
    closePaymentModal();
    await fetchPlans();
    await onPlanChanged?.();
  };

  return (
    <div className="subscription-plans-card">
      <div className="subscription-plans-header">
        <h3 className="subscription-plans-title">Your Plans</h3>
        <p className="subscription-plans-subtitle">
          Switch to a different plan whenever your portfolio changes.
        </p>
      </div>

      {loading ? (
        <div className="plan-card-grid">
          {[0, 1, 2].map((index) => (
            <div key={index} className="plan-card plan-card-skeleton" />
          ))}
        </div>
      ) : !plans.length ? (
        <p className="plan-select-error">
          {initError || 'No subscription plans are available right now.'}
        </p>
      ) : (
        <div className="plan-card-grid">
          {plans.map((plan) => {
            const interval = formatInterval(plan.interval);
            const details = getTierDetails(plan);
            const isActive = isCurrentPlan(plan);
            const isPending = pendingPlanId === plan.id;

            return (
              <div
                key={plan.id}
                className={`plan-card${isActive ? ' plan-card-active' : ''}${isPending ? ' pending' : ''}`}
              >
                {isActive && <span className="plan-card-badge">Activated</span>}

                <div className="plan-card-name">{plan.name}</div>

                <div className="plan-card-pricing">
                  <span className="plan-card-price">{formatPrice(plan.price)}</span>
                  <span className="plan-card-interval">/{interval}</span>
                </div>

                <div className="plan-card-range">
                  {details?.range || `${formatPrice(plan.price)} per ${interval}`}
                </div>
                {details?.tagline && <div className="plan-card-tagline">{details.tagline}</div>}

                {isActive ? (
                  <div className="plan-card-active-label">Activated</div>
                ) : (
                  <Button
                    type="button"
                    variant="success"
                    className="plan-card-button"
                    disabled={Boolean(pendingPlanId)}
                    onClick={() => openConfirmModal(plan)}
                    aria-label={`Change to the ${plan.name} plan`}
                  >
                    {isPending ? 'Please wait...' : 'Change Plan'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Change Plan confirmation modal */}
      <Modal
        isOpen={Boolean(confirmPlan)}
        onRequestClose={closeConfirmModal}
        contentLabel="Change plan"
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className="logout-modal"
        overlayClassName="logout-modal-overlay"
      >
        <button className="logout-modal-close" onClick={closeConfirmModal} type="button" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h2 className="logout-modal-title">Change plan</h2>
        <p className="logout-modal-message">
          Are you sure you want to switch to the {confirmPlan?.name} plan
          {confirmPlan ? ` (${formatPrice(confirmPlan.price)}/${formatInterval(confirmPlan.interval)})` : ''}?
        </p>
        <div className="logout-modal-buttons">
          <button type="button" className="logout-modal-cancel" onClick={closeConfirmModal}>Cancel</button>
          <button type="button" className="logout-modal-confirm" onClick={confirmChangePlan}>OK</button>
        </div>
      </Modal>

      {/* Stripe payment modal */}
      <Modal
        isOpen={paymentModalOpen}
        onRequestClose={closePaymentModal}
        contentLabel="Complete payment"
        shouldCloseOnOverlayClick={false}
        ariaHideApp={false}
        className="plan-payment-modal"
        overlayClassName="plan-payment-modal-overlay"
      >
        <button
          type="button"
          className="plan-payment-modal-close"
          onClick={closePaymentModal}
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <PlanPaymentForm
              plan={paymentPlan}
              clientSecret={clientSecret}
              mode={paymentMode}
              onPaymentSuccess={handlePaymentSuccess}
              onCancel={closePaymentModal}
            />
          </Elements>
        ) : (
          <div className="payment-form-section">
            <h2 className="payment-form-title">Complete Payment</h2>
            <p className="plan-select-error">
              Stripe is not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in .env
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;
